import {
  getClientReceiptEmail, saveClientReceiptEmail,
  type ReceiptOptionsDto, type ClientReceiptEmailDto, type ClientReceiptEmailContextDto,
  type SaveClientReceiptEmailPreference,
} from "./api.ts";
import type { Session } from "./session.ts";
interface PayerContext { orderId:string; paymentAttemptId?:string }
interface Host {
  session():Session|undefined;
  payer():PayerContext|undefined;
  updated():Promise<void>;
}
function element<T extends HTMLElement>(id:string):T {
  const found = document.getElementById(id);
  if(found === null)throw new Error(`Missing receipt control ${id}`);
  return found as T;
}
/** Current preference only. A receipt taker gets a masked hint; full address editing remains manager-authorized. */
export function mountClientReceiptEmail(host:Host,emailInput:HTMLInputElement,sendButton:HTMLButtonElement) {
  const open = element<HTMLButtonElement>("client-receipt-email-open");
  const editor = element<HTMLElement>("client-receipt-email-editor");
  const value = element<HTMLInputElement>("client-receipt-email-value");
  const message = element<HTMLElement>("client-receipt-email-status");
  const save = element<HTMLButtonElement>("client-receipt-email-save");
  const clear = element<HTMLButtonElement>("client-receipt-email-clear");
  const stop = element<HTMLButtonElement>("client-receipt-email-stop");
  const cancel = element<HTMLButtonElement>("client-receipt-email-cancel");
  const choice = element<HTMLElement>("client-receipt-email-save-choice");
  const keep = element<HTMLInputElement>("client-receipt-email-keep");
  let epoch = 0;
  let context:ClientReceiptEmailContextDto|null = null;
  let loaded:ClientReceiptEmailDto|undefined;
  let pendingWrite:{fingerprint:string;key:string}|undefined;
  function capture() {const session = host.session();const payer = host.payer();return session === undefined || payer === undefined ? undefined : {session,payer};}
  function active(stamp:number,held:NonNullable<ReturnType<typeof capture>>):boolean {
    const current = host.session();const payer = host.payer();
    return stamp === epoch && current?.token === held.session.token && current.tenantId === held.session.tenantId
      && payer?.orderId === held.payer.orderId && payer.paymentAttemptId === held.payer.paymentAttemptId;
  }
  function sendLabel() {sendButton.textContent = context !== null && keep.checked ? "Send and save" : "Send";}
  function busy(disabled:boolean) {for(const control of [open,value,save,clear,cancel,stop])control.disabled = disabled;stop.disabled = disabled || loaded?.email == null || loaded.suppressed === true || value.value.trim() !== loaded.email;}
  function reset():void {
    epoch += 1;context = null;loaded = undefined;pendingWrite = undefined;
    stop.hidden = true;open.hidden = true;editor.hidden = true;choice.hidden = true;keep.checked = true;value.value = "";emailInput.value = "";sendButton.disabled = true;message.textContent = "";busy(false);sendLabel();
  }
  function apply(options:ReceiptOptionsDto|undefined):void {
    const next = options?.clientEmail ?? null;
    if(next?.identity !== context?.identity) {keep.checked = true;loaded = undefined;pendingWrite = undefined;value.value = "";emailInput.value = "";sendButton.disabled = true;editor.hidden = true;busy(false);epoch += 1;}
    context = next;open.hidden = next?.canManage !== true;choice.hidden = next === null;sendLabel();
  }
  function preference():SaveClientReceiptEmailPreference {
    if(context === null || emailInput.value.trim() === "")return {};
    return keep.checked ? {saveClientEmail:true,clientEmailIdentity:context.identity,clientEmailRevision:context.revision} : {saveClientEmail:false};
  }
  keep.onchange = sendLabel;
  open.onclick = () => {
    const held = capture();if(held === undefined || context?.canManage !== true)return;
    const identity = context.identity;const stamp = ++epoch;
    editor.hidden = false;value.value = "";loaded = undefined;message.textContent = "Loading";busy(true);
    void getClientReceiptEmail(held.session.apiBaseUrl,held.session.tenantId,held.payer.orderId,held.payer.paymentAttemptId).then(reply => {
      if(!active(stamp,held))return;
      if(reply.status !== 200 || reply.body.identity !== identity) {message.textContent = reply.body?.message ?? "Client email changed. Reopen the receipt choices.";return;}
      loaded = reply.body;context = {identity:loaded.identity,revision:loaded.revision,canManage:loaded.canManage};
      value.value = loaded.email ?? "";stop.hidden = loaded.email === null || loaded.suppressed === true;message.textContent = loaded.suppressed === true ? "Receipt email stopped" : "";
    }).catch(() => {if(active(stamp,held))message.textContent = "Could not read the client email.";})
    .finally(() => {if(active(stamp,held))busy(false);});
  };
  async function write(address:string|null,optOut=false):Promise<void> {
    const held = capture();const prior = loaded;
    if(held === undefined || prior === undefined || !prior.canManage)return;
    if(!optOut && address !== null && (address === "" || !value.validity.valid)) {message.textContent = "Enter an email address.";return;}
    if(optOut && (prior.email === null || prior.suppressed === true || value.value.trim() !== prior.email))return;
    const stamp = epoch;const fingerprint = JSON.stringify([held.payer,prior.identity,prior.revision,address,optOut]);
    if(pendingWrite === undefined || pendingWrite.fingerprint !== fingerprint)pendingWrite = {fingerprint,key:crypto.randomUUID()};
    busy(true);message.textContent = "";
    try {
      const reply = await saveClientReceiptEmail(held.session.apiBaseUrl,held.session.tenantId,held.payer.orderId,{
        ...(optOut ? {optOut:true as const} : {email:address}),expectedIdentity:prior.identity,expectedRevision:prior.revision,idempotencyKey:pendingWrite.key,
        ...(held.payer.paymentAttemptId === undefined ? {} : {paymentAttemptId:held.payer.paymentAttemptId}),
      });
      if(!active(stamp,held))return;
      if(reply.status !== 200) {message.textContent = reply.body?.message ?? "Could not save the client email.";return;}
      pendingWrite = undefined;loaded = reply.body;context = {identity:loaded.identity,revision:loaded.revision,canManage:loaded.canManage};
      editor.hidden = true;value.value = "";
      try {await host.updated();}
      catch {if(active(stamp,held)){editor.hidden = false;message.textContent = "Email saved. Reopen the receipt choices.";}}
    }catch {if(active(stamp,held))message.textContent = "Could not save the client email.";}
    finally {if(active(stamp,held))busy(false);}
  }
  value.oninput = () => {stop.disabled = loaded?.email == null || loaded.suppressed === true || value.value.trim() !== loaded.email;};
  stop.onclick = () => {void write(null,true);};
  save.onclick = () => {void write(value.value.trim());};
  clear.onclick = () => {void write(null);};
  cancel.onclick = () => {epoch += 1;editor.hidden = true;value.value = "";loaded = undefined;message.textContent = "";busy(false);};
  reset();
  return {reset,apply,preference,refresh:host.updated};
}
